/**
 * The instructor assessment form — UX/UI Specification §9.10.
 *
 * Shared by the student profile and the report wizard. It is the same form in
 * both places on purpose: an instructor who spots a wrong score while building
 * a report should be able to fix it there, and a second, slightly different
 * form would eventually disagree with the first about what is optional.
 *
 * Every score is optional. An instructor scores what they actually watched
 * that session, and the ones they did not are simply absent.
 */

import { useState } from 'react';
import {
  ASSESSMENT_KEYS,
  ASSESSMENT_LABELS,
  type AssessmentKey,
  type AssessmentRecord,
} from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from './ui/Button.js';
import './AssessmentForm.css';

type Scores = Partial<Record<AssessmentKey, number | null>>;

const SCORES = [1, 2, 3, 4, 5] as const;

export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function scoresOf(initial: AssessmentRecord | null): Scores {
  if (!initial) return {};
  return Object.fromEntries(
    ASSESSMENT_KEYS.map((key) => [key, initial[key as keyof AssessmentRecord] ?? null]),
  ) as Scores;
}

export function AssessmentForm({
  studentId,
  initial = null,
  onSaved,
  onCancel,
}: {
  studentId: string;
  /** Present when editing; absent when adding. */
  initial?: AssessmentRecord | null;
  onSaved: (assessment: AssessmentRecord) => void;
  onCancel: () => void;
}) {
  const [assessedAt, setAssessedAt] = useState(initial?.assessedAt ?? todayIso());
  const [scores, setScores] = useState<Scores>(() => scoresOf(initial));
  const [generalNote, setGeneralNote] = useState(initial?.generalNote ?? '');
  const [nextFocus, setNextFocus] = useState(initial?.nextPeriodFocus ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const hasAnything =
    ASSESSMENT_KEYS.some((key) => scores[key] != null) ||
    generalNote.trim() !== '' ||
    nextFocus.trim() !== '';

  function setScore(key: AssessmentKey, value: number) {
    setScores((current) => ({ ...current, [key]: value }));
  }

  /**
   * Clearing is a separate control rather than re-clicking the chosen segment.
   *
   * A radio fires both click and change, so a toggle handler ran twice and
   * cancelled itself. Re-clicking to clear is also non-standard for radios,
   * and an instructor who mis-tapped needs a visible way out.
   */
  function clearScore(key: AssessmentKey) {
    setScores((current) => ({ ...current, [key]: null }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    const body = {
      assessedAt,
      coreControl: scores.coreControl ?? null,
      balance: scores.balance ?? null,
      flexibility: scores.flexibility ?? null,
      lowerBodyStrength: scores.lowerBodyStrength ?? null,
      upperBodyControl: scores.upperBodyControl ?? null,
      generalNote: generalNote.trim() || null,
      nextPeriodFocus: nextFocus.trim() || null,
    };

    try {
      const base = `/api/students/${studentId}/assessments`;
      if (initial) await api.patch(`${base}/${initial.id}`, body);
      else await api.post(base, body);
      onSaved({ id: initial?.id ?? '', ...body });
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="assess__form" noValidate>
      <label className="assess__date">
        <span>Değerlendirme Tarihi</span>
        <input
          type="date"
          value={assessedAt}
          max={todayIso()}
          onChange={(e) => setAssessedAt(e.target.value)}
          required
        />
      </label>

      <div className="assess__scores">
        {ASSESSMENT_KEYS.map((key) => (
          <fieldset key={key} className="assess__row">
            <legend className="assess__label">{ASSESSMENT_LABELS[key]}</legend>
            <div className="assess__segments">
              {SCORES.map((value) => (
                <label
                  key={value}
                  className={scores[key] === value ? 'segment segment--on' : 'segment'}
                >
                  <input
                    type="radio"
                    name={`${initial?.id ?? 'new'}-${key}`}
                    value={value}
                    checked={scores[key] === value}
                    onChange={() => setScore(key, value)}
                    className="pp-visually-hidden"
                  />
                  {value}
                </label>
              ))}
              <button
                type="button"
                className="assess__clear"
                onClick={() => clearScore(key)}
                disabled={scores[key] == null}
                aria-label={`${ASSESSMENT_LABELS[key]} puanını temizle`}
              >
                Temizle
              </button>
            </div>
          </fieldset>
        ))}
      </div>

      <label className="assess__field">
        <span>Genel Değerlendirme</span>
        <textarea
          rows={3}
          maxLength={1000}
          value={generalNote}
          onChange={(e) => setGeneralNote(e.target.value)}
        />
      </label>

      <label className="assess__field">
        <span>Bir Sonraki Dönem Odağı</span>
        <textarea
          rows={2}
          maxLength={1000}
          value={nextFocus}
          onChange={(e) => setNextFocus(e.target.value)}
        />
      </label>

      {error && (
        <p className="assess__error" role="alert">
          {error}
        </p>
      )}

      <div className="assess__actions">
        <p className="assess__hint">
          {hasAnything
            ? 'Puan vermediğiniz başlıklar boş kalır.'
            : 'Kaydetmek için en az bir puan veya not girin.'}
        </p>
        <div className="assess__buttons">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Vazgeç
          </Button>
          <Button type="submit" loading={busy} disabled={!hasAnything}>
            Kaydet
          </Button>
        </div>
      </div>
    </form>
  );
}
